import { useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  updateDossier,
  updateDossierValues,
} from "../../redux/features/dossiers/dossierSlice";

import DynamicFieldRenderer from "./DynamicFieldRenderer";
import DossierStepNavigation from "./DossierStepNavigation";

import "./DossierDynamicForm.css";

const STEP_LABELS = {
  aanmelding: "Aanmelding",
  administratie: "Administratie",
  begeleiding: "Begeleiding",
  vdab: "VDAB",
  kld: "KLD",
  afronding: "Afronding",
};

const STEP_ORDER = [
  "aanmelding",
  "administratie",
  "begeleiding",
  "vdab",
  "kld",
  "afronding",
];

const normalizeDossierValues = (values = {}) => {
  const normalized = {};

  Object.entries(values || {}).forEach(([key, rawValue]) => {
    if (rawValue && typeof rawValue === "object" && "value" in rawValue) {
      normalized[key] = rawValue.value;
    } else {
      normalized[key] = rawValue;
    }
  });

  return normalized;
};

const canSeeField = (field, user) => {
  if (user?.role === "POWER_ADMIN") return true;

  if (!Array.isArray(field.visible_for) || field.visible_for.length === 0) {
    return true;
  }

  return field.visible_for.includes(user?.role);
};

const canEditField = (field, user, dossier) => {
  if (!field || !user || !dossier) return false;

  if (user.role === "POWER_ADMIN") return true;

  if (field.step !== dossier.current_step) return false;

  if (field.is_readonly || field.read_only) return false;

  if (!Array.isArray(field.editable_by) || field.editable_by.length === 0) {
    return true;
  }

  return field.editable_by.includes(user.role);
};

const groupByGroup = (fields = []) => {
  const grouped = {};

  fields.forEach((field) => {
    const group = field.group || "general";

    if (!grouped[group]) {
      grouped[group] = [];
    }

    grouped[group].push(field);
  });

  return grouped;
};

const isEmpty = (value) => {
  return (
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );
};

const validateField = (field, value) => {
  if (field.required && isEmpty(value)) {
    return `${field.label} is verplicht.`;
  }

  if (isEmpty(value)) return "";

  if (field.type === "email" && !/^\S+@\S+\.\S+$/.test(value)) {
    return "Ongeldig e-mailadres.";
  }

  if (field.type === "number" && Number.isNaN(Number(value))) {
    return "Waarde moet een nummer zijn.";
  }

  if (field.validation?.min !== null && field.validation?.min !== undefined) {
    if (Number(value) < Number(field.validation.min)) {
      return `Minimumwaarde is ${field.validation.min}.`;
    }
  }

  if (field.validation?.max !== null && field.validation?.max !== undefined) {
    if (Number(value) > Number(field.validation.max)) {
      return `Maximumwaarde is ${field.validation.max}.`;
    }
  }

  if (
    field.validation?.min_length !== null &&
    field.validation?.min_length !== undefined
  ) {
    if (String(value).length < Number(field.validation.min_length)) {
      return `Minimale lengte is ${field.validation.min_length}.`;
    }
  }

  if (
    field.validation?.max_length !== null &&
    field.validation?.max_length !== undefined
  ) {
    if (String(value).length > Number(field.validation.max_length)) {
      return `Maximale lengte is ${field.validation.max_length}.`;
    }
  }

  if (field.validation?.regex) {
    try {
      const regex = new RegExp(field.validation.regex);

      if (!regex.test(String(value))) {
        return "Waarde voldoet niet aan het vereiste formaat.";
      }
    } catch {
      return "Ongeldige regex configuratie.";
    }
  }

  return "";
};

const getChangedValues = (initialValues, currentValues) => {
  const changed = {};

  Object.keys(currentValues).forEach((key) => {
    const before = JSON.stringify(initialValues[key] ?? null);
    const after = JSON.stringify(currentValues[key] ?? null);

    if (before !== after) {
      changed[key] = currentValues[key];
    }
  });

  return changed;
};

export default function DossierDynamicForm({ dossier, fields = [] }) {
  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.auth);
  const { isUpdating } = useSelector((state) => state.dossiers);

  const [formValues, setFormValues] = useState({});
  const [initialValues, setInitialValues] = useState({});
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [activeStep, setActiveStep] = useState(
    dossier?.current_step || "aanmelding",
  );

  useEffect(() => {
    const normalized = normalizeDossierValues(dossier?.values || {});

    setFormValues(normalized);
    setInitialValues(normalized);
    setErrors({});
    setSuccessMessage("");
    setActiveStep(dossier?.current_step || "aanmelding");
  }, [dossier]);

  const visibleFields = useMemo(() => {
    return fields
      .filter((field) => field.is_active !== false)
      .filter((field) => canSeeField(field, user))
      .sort((a, b) => {
        const stepA = STEP_ORDER.indexOf(a.step);
        const stepB = STEP_ORDER.indexOf(b.step);

        if (stepA !== stepB) return stepA - stepB;

        const groupCompare = String(a.group || "").localeCompare(
          String(b.group || ""),
        );

        if (groupCompare !== 0) return groupCompare;

        return (a.order || 0) - (b.order || 0);
      });
  }, [fields, user]);

  const steps = useMemo(() => {
    const existingSteps = [
      ...new Set(visibleFields.map((field) => field.step)),
    ];

    return STEP_ORDER.filter((step) => existingSteps.includes(step));
  }, [visibleFields]);

  const activeStepFields = useMemo(() => {
    return visibleFields.filter((field) => field.step === activeStep);
  }, [visibleFields, activeStep]);

  const groupedActiveFields = useMemo(() => {
    return groupByGroup(activeStepFields);
  }, [activeStepFields]);

  const changedValues = useMemo(() => {
    return getChangedValues(initialValues, formValues);
  }, [initialValues, formValues]);

  const hasChanges = Object.keys(changedValues).length > 0;

  const canChangeStep = user?.role === "POWER_ADMIN";

  const handleFieldChange = (key, value) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [key]: "",
    }));

    setSuccessMessage("");
  };

  const validateForm = () => {
    const nextErrors = {};

    activeStepFields
      .filter((field) => canEditField(field, user, dossier))
      .forEach((field) => {
        const error = validateField(field, formValues[field.key]);

        if (error) {
          nextErrors[field.key] = error;
        }
      });

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    if (!validateForm()) return;

    await dispatch(
      updateDossierValues({
        id: dossier._id,
        values: changedValues,
      }),
    ).unwrap();

    setInitialValues(formValues);
    setSuccessMessage("Wijzigingen opgeslagen.");
  };

  const handleReset = () => {
    setFormValues(initialValues);
    setErrors({});
    setSuccessMessage("");
  };

  const handleChangeCurrentStep = async (nextStep) => {
    if (!canChangeStep || !nextStep || nextStep === dossier.current_step)
      return;

    await dispatch(
      updateDossier({
        id: dossier._id,
        data: {
          current_step: nextStep,
        },
      }),
    ).unwrap();

    setSuccessMessage(
      `Stap gewijzigd naar ${STEP_LABELS[nextStep] || nextStep}.`,
    );
  };

  if (steps.length === 0) {
    return (
      <div className="dossier-dynamic-form__empty">
        Geen zichtbare velden gevonden.
      </div>
    );
  }

  return (
    <div className="dossier-dynamic-form-layout">
      <aside className="dossier-dynamic-form-layout__side">
        <DossierStepNavigation
          steps={steps}
          activeStep={activeStep}
          currentStep={dossier.current_step}
          onSelectStep={setActiveStep}
          canChangeStep={canChangeStep}
          onChangeCurrentStep={handleChangeCurrentStep}
          isUpdating={isUpdating}
        />
      </aside>

      <section className="dossier-dynamic-form">
        <div className="dossier-dynamic-form__toolbar">
          <div>
            <h2>{STEP_LABELS[activeStep] || activeStep}</h2>

            <p>
              {activeStep === dossier.current_step
                ? "Deze stap is momenteel actief."
                : user?.role === "POWER_ADMIN"
                  ? "Admin kan ook buiten de huidige stap wijzigen."
                  : "Deze stap is alleen-lezen tot de admin de stap wijzigt."}
            </p>
          </div>

          <div className="dossier-dynamic-form__toolbar-actions">
            <button
              type="button"
              className="dossier-dynamic-form__secondary"
              disabled={!hasChanges || isUpdating}
              onClick={handleReset}
            >
              Reset
            </button>

            <button
              type="button"
              className="dossier-dynamic-form__primary"
              disabled={!hasChanges || isUpdating}
              onClick={handleSave}
            >
              {isUpdating ? "Opslaan..." : "Wijzigingen opslaan"}
            </button>
          </div>
        </div>

        {successMessage && (
          <div className="dossier-dynamic-form__success">{successMessage}</div>
        )}

        {Object.keys(groupedActiveFields).length === 0 ? (
          <div className="dossier-dynamic-form__empty">
            Geen velden in deze stap.
          </div>
        ) : (
          <div className="dossier-dynamic-form__groups">
            {Object.entries(groupedActiveFields).map(
              ([groupKey, groupFields]) => (
                <div key={groupKey} className="dossier-dynamic-form__group">
                  <div className="dossier-dynamic-form__group-header">
                    <h4>{groupKey}</h4>
                    <span>{groupFields.length} velden</span>
                  </div>

                  <div className="dossier-dynamic-form__fields">
                    {groupFields.map((field) => (
                      <DynamicFieldRenderer
                        key={field._id || field.key}
                        field={field}
                        value={formValues[field.key]}
                        onChange={handleFieldChange}
                        error={errors[field.key]}
                        disabled={
                          !canEditField(field, user, dossier) || isUpdating
                        }
                      />
                    ))}
                  </div>
                </div>
              ),
            )}
          </div>
        )}
      </section>
    </div>
  );
}
